import { type Common } from './common.js';
import {
  type BaseEhrProviderInformation,
  type EhrProviderInformation,
  type EhrProviderInformationPopulated,
  type FindAllEhrProviderInformationParams,
} from './models/ehrProviderInformation.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';

export class API {
  common: Common;
  baseEndpoint = '/ehr-provider-information';

  constructor(common: Common) {
    this.common = common;
  }

  async findOneById(id: string): Promise<EhrProviderInformationPopulated> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async findAll(query: { providerUserId?: string }): Promise<EhrProviderInformation[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, query);
    return await this.common.getJSON(url);
  }

  async findAllByPracticeIdWithProviders(
    practiceId: string
  ): Promise<EhrProviderInformationPopulated[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/providers`, {
      practiceId,
    });
    return await this.common.getJSON(url);
  }

  async findAllByPracticeIdWithProvidersPaginated(
    practiceId: string,
    options: PaginatedQueryOptions<FindAllEhrProviderInformationParams>
  ): Promise<PaginationResponse<EhrProviderInformationPopulated>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/providers`, {
      practiceId,
      ...options,
    });
    return await this.common.getJSON(url);
  }

  async upsert(
    ehrProviderInformation: BaseEhrProviderInformation
  ): Promise<EhrProviderInformation> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.putJSON(url, ehrProviderInformation);
  }

  async remove(ehrProviderInformationId: string): Promise<void> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${ehrProviderInformationId}`);
    return await this.common.deleteJSON(url);
  }

  async update(
    ehrProviderInformationId: string,
    values: Partial<EhrProviderInformation>
  ): Promise<EhrProviderInformation> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${ehrProviderInformationId}`);
    return await this.common.patchJSON(url, values);
  }
}
